import { useQuery } from '@tanstack/react-query'
import { UserRoundCheck } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toApiError } from '@/api/errors'
import { toast } from '@/components/feedback/toast-store'
import { FormField } from '@/components/forms/FormField'
import { Alert } from '@/components/ui/Alert'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { Dialog } from '@/components/ui/Dialog'
import { Input } from '@/components/ui/Input'
import { RadioGroup } from '@/components/ui/RadioGroup'
import { Select } from '@/components/ui/Select'
import { Skeleton } from '@/components/ui/Skeleton'
import { Textarea } from '@/components/ui/Textarea'
import type { SchoolClass } from '@/features/academic/types'
import { todayIso } from '@/features/attendance/dates'
import { formatDate } from '@/utils/format'
import { coverCandidatesQuery, useCreateCover } from '../api'
import type { CoverCandidate, CoverKind } from '../types'
import { weekdayName } from '../week'
import { ResourcePicker, type PickerOption } from './ResourcePicker'

export interface CoverDraft {
  kind?: CoverKind
  classId?: number
  sectionId?: number
  subjectId?: number
  startDate?: string
  endDate?: string
  leaveRequestId?: number
  /** Shown in the description when the cover is for someone's leave. */
  teacherName?: string
}

interface CoverDialogProps {
  classes: SchoolClass[]
  draft?: CoverDraft
  onOpenChange: (open: boolean) => void
}

const FIELDS = ['kind', 'sectionId', 'subjectId', 'substituteId', 'startDate', 'endDate', 'note'] as const

/** Arrange a substitute for a class teacher's duties or a subject's lessons, between two dates. */
export function CoverDialog({ classes, draft = {}, onOpenChange }: CoverDialogProps) {
  const { t } = useTranslation('timetable')
  const create = useCreateCover()
  const [kind, setKind] = useState<CoverKind>(draft.kind ?? 'lesson')
  const [classId, setClassId] = useState(draft.classId ?? 0)
  const [sectionId, setSectionId] = useState(draft.sectionId ?? 0)
  const [subjectId, setSubjectId] = useState(draft.subjectId ?? 0)
  const [startDate, setStartDate] = useState(draft.startDate ?? todayIso())
  const [endDate, setEndDate] = useState(draft.endDate ?? draft.startDate ?? todayIso())
  const [substituteId, setSubstituteId] = useState('')
  const [note, setNote] = useState('')
  const [errors, setErrors] = useState<Partial<Record<(typeof FIELDS)[number], string>>>({})
  const [formError, setFormError] = useState<string | null>(null)

  const schoolClass = classes.find((c) => c.id === classId)
  const datesValid = !!startDate && !!endDate && endDate >= startDate
  const ready = sectionId > 0 && datesValid && (kind === 'section' || subjectId > 0)
  const candidates = useQuery({
    ...coverCandidatesQuery({
      kind,
      sectionId,
      subjectId: kind === 'lesson' ? subjectId : undefined,
      startDate,
      endDate,
    }),
    enabled: ready,
  })
  const selected = candidates.data?.candidates.find((c) => String(c.id) === substituteId)

  const resetSubstitute = () => setSubstituteId('')
  const submit = async () => {
    setErrors({})
    setFormError(null)
    try {
      const cover = await create.mutateAsync({
        kind,
        sectionId,
        subjectId: kind === 'lesson' ? subjectId : null,
        substituteId: Number(substituteId),
        startDate,
        endDate,
        note: note.trim() || null,
        leaveRequestId: draft.leaveRequestId ?? null,
      })
      toast.success(
        t('Cover arranged'),
        t('{{name}} covers {{what}} in {{section}}.', {
          name: cover.substitute.name,
          what: kind === 'section' ? t('the class teacher duties') : (cover.subject?.name ?? ''),
          section: cover.section.name,
        }),
      )
      onOpenChange(false)
    } catch (e) {
      const apiError = toApiError(e)
      const fieldErrors: typeof errors = {}
      for (const v of apiError.violations) {
        if ((FIELDS as readonly string[]).includes(v.field))
          fieldErrors[v.field as (typeof FIELDS)[number]] = v.message
      }
      setErrors(fieldErrors)
      if (Object.keys(fieldErrors).length === 0) setFormError(apiError.message)
    }
  }

  const options: PickerOption[] = (candidates.data?.candidates ?? []).map((c) => ({
    value: String(c.id),
    label: c.name,
    disabled: !c.available,
    description: candidateNote(c, t),
    badges: (
      <>
        {c.subjectMatch ? (
          <Badge tone="brand" size="sm">
            {t('Same subject')}
          </Badge>
        ) : null}
        {c.knowsSection ? (
          <Badge tone="neutral" size="sm">
            {t('Teaches this class')}
          </Badge>
        ) : null}
        {c.onLeave ? (
          <Badge tone="warning" size="sm">
            {t('On leave')}
          </Badge>
        ) : c.busyWith ? (
          <Badge tone="danger" size="sm">
            {t('Busy')}
          </Badge>
        ) : (
          <Badge tone="success" size="sm">
            {t('Free')}
          </Badge>
        )}
      </>
    ),
  }))

  return (
    <Dialog
      open
      onOpenChange={onOpenChange}
      size="lg"
      icon={<UserRoundCheck />}
      title={t('Arrange cover')}
      description={
        draft.teacherName
          ? t('A substitute takes over from {{name}} on these days only; {{name}} keeps their role.', {
              name: draft.teacherName,
            })
          : t('A substitute takes over on these days only; the usual teacher keeps their role.')
      }
      dismissible={!create.isPending}
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)} disabled={create.isPending}>
            {t('Cancel')}
          </Button>
          <Button onClick={() => void submit()} loading={create.isPending} disabled={!ready || !selected}>
            {t('Arrange cover')}
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        {formError ? (
          <Alert tone="danger" title={t('Cover was not arranged')}>
            {formError}
          </Alert>
        ) : null}
        <FormField label={t('What is covered')} error={errors.kind}>
          <RadioGroup
            appearance="cards"
            value={kind}
            onValueChange={(v) => {
              setKind(v as CoverKind)
              resetSubstitute()
            }}
            options={[
              {
                value: 'lesson',
                label: t('A subject’s lessons'),
                description: t(
                  'The lessons appear in the substitute’s timetable; they can enter that subject’s marks.',
                ),
              },
              {
                value: 'section',
                label: t('Class teacher duties'),
                description: t('The register, the roster and every subject’s marks of the section.'),
              },
            ]}
          />
        </FormField>
        <div className="grid gap-4 sm:grid-cols-2">
          <FormField label={t('Class')} required>
            <Select
              options={classes.map((c) => ({ value: c.id, label: c.name }))}
              placeholder={t('Choose a class')}
              value={classId || ''}
              onChange={(e) => {
                const next = classes.find((c) => c.id === Number(e.target.value))
                setClassId(next?.id ?? 0)
                setSectionId(next?.sections[0]?.id ?? 0)
                setSubjectId(0)
                resetSubstitute()
              }}
            />
          </FormField>
          <FormField label={t('Section')} required error={errors.sectionId}>
            <Select
              options={(schoolClass?.sections ?? []).map((s) => ({
                value: s.id,
                label: [s.name || t('Whole class'), s.classTeacher?.name].filter(Boolean).join(' · '),
              }))}
              placeholder={t('Choose a section')}
              value={sectionId || ''}
              disabled={!schoolClass}
              onChange={(e) => {
                setSectionId(Number(e.target.value))
                resetSubstitute()
              }}
            />
          </FormField>
          {kind === 'lesson' ? (
            <FormField label={t('Subject')} required error={errors.subjectId} className="sm:col-span-2">
              <Select
                options={(schoolClass?.subjects ?? []).map((s) => ({ value: s.subjectId, label: s.name }))}
                placeholder={t('Choose a subject')}
                value={subjectId || ''}
                disabled={!schoolClass}
                onChange={(e) => {
                  setSubjectId(Number(e.target.value))
                  resetSubstitute()
                }}
              />
            </FormField>
          ) : null}
          <FormField label={t('First day')} required error={errors.startDate}>
            <Input
              type="date"
              value={startDate}
              onChange={(e) => {
                setStartDate(e.target.value)
                if (endDate < e.target.value) setEndDate(e.target.value)
                resetSubstitute()
              }}
            />
          </FormField>
          <FormField
            label={t('Last day')}
            required
            error={
              errors.endDate ??
              (!datesValid && endDate ? t('The last day must be on or after the first day.') : undefined)
            }
          >
            <Input
              type="date"
              min={startDate}
              value={endDate}
              onChange={(e) => {
                setEndDate(e.target.value)
                resetSubstitute()
              }}
            />
          </FormField>
        </div>

        <FormField
          label={t('Substitute')}
          required
          error={errors.substituteId}
          hint={
            candidates.data
              ? [
                  candidates.data.absentTeacher
                    ? t('Covering for {{name}}', { name: candidates.data.absentTeacher.name })
                    : null,
                  kind === 'lesson'
                    ? t('{{count}} lessons a week', { count: candidates.data.lessonsPerWeek })
                    : null,
                  t('{{count}} school days', { count: candidates.data.workingDays }),
                ]
                  .filter(Boolean)
                  .join(' · ')
              : undefined
          }
        >
          {!ready ? (
            <p className="rounded-control border border-dashed border-line px-3 py-4 text-center text-sm text-muted">
              {t('Choose the section, subject and dates to see who is free.')}
            </p>
          ) : candidates.isPending ? (
            <div className="space-y-2" aria-busy="true">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : candidates.error ? (
            <Alert tone="danger" title={t('Could not load the teachers')}>
              {toApiError(candidates.error).message}
            </Alert>
          ) : options.length === 0 ? (
            <p className="text-sm text-muted">{t('No other teacher can take this.')}</p>
          ) : (
            <ResourcePicker
              value={substituteId}
              onValueChange={setSubstituteId}
              options={options}
              aria-label={t('Substitute')}
            />
          )}
        </FormField>
        {kind === 'lesson' && candidates.data?.lessonsPerWeek === 0 ? (
          <Alert tone="info" title={t('No lessons scheduled')}>
            {t(
              'This subject has no lessons in the section’s timetable, so nothing will appear in the substitute’s week — they can still enter its marks.',
            )}
          </Alert>
        ) : null}

        <FormField
          label={t('Note')}
          error={errors.note}
          hint={t('Optional, e.g. the reason or instructions.')}
        >
          <Textarea rows={2} maxLength={255} value={note} onChange={(e) => setNote(e.target.value)} />
        </FormField>
      </div>
    </Dialog>
  )
}

function candidateNote(
  c: CoverCandidate,
  t: (key: string, options?: Record<string, unknown>) => string,
): string {
  if (c.onLeave) {
    return t('On leave {{from}} → {{to}}', {
      from: formatDate(c.onLeave.startDate),
      to: formatDate(c.onLeave.endDate),
    })
  }
  if (c.busyWith) {
    return t('Teaches {{section}} on {{day}} ({{period}})', {
      section: c.busyWith.section,
      day: weekdayName(c.busyWith.dayOfWeek),
      period: c.busyWith.period,
    })
  }
  return c.primarySubject ? c.primarySubject.name : c.employeeCode
}
